import { useEffect, useRef } from 'react';
import { requestPoll, subscribePoll, type PollSpec } from '../utils/sharedPolling';

type Options = {
  method?: 'GET' | 'POST';
  body?: any;
  /**
   * Continua pedindo mesmo com a aba escondida. Use só quando parar tem efeito
   * colateral no servidor — o caso do heartbeat, que se parar marca o usuário
   * como offline (ONLINE_TIMEOUT = 30s). O compartilhamento entre abas continua
   * valendo, então mesmo assim é 1 requisição em vez de N.
   */
  runWhenHidden?: boolean;
};

/**
 * Substitui o padrão `fetch() + setInterval()` dos polls.
 *
 * Duas coisas mudam em relação ao setInterval solto:
 *  1. aba escondida não pede nada (e pede na hora que volta ao foco)
 *  2. abas do mesmo navegador dividem a mesma requisição (ver utils/sharedPolling)
 *
 * `onData` recebe o JSON já parseado — a mesma coisa que o `await res.json()`
 * de antes. Não precisa ser estável: é lido por ref.
 *
 * Passe `key = null` para desligar (ex.: enquanto não tem fluxo selecionado).
 */
export function useSharedPoll(
  key: string | null,
  url: string,
  intervalMs: number,
  onData: (data: any) => void,
  options?: Options
) {
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const method = options?.method;
  const runWhenHidden = !!options?.runWhenHidden;
  // Serializado pra poder entrar nas deps sem recriar o efeito a cada render.
  const bodyKey = options?.body === undefined ? '' : JSON.stringify(options.body);

  useEffect(() => {
    if (!key) return;

    const spec: PollSpec = {
      key,
      url,
      intervalMs,
      method,
      body: bodyKey ? JSON.parse(bodyKey) : undefined,
    };

    const unsubscribe = subscribePoll(key, (data) => onDataRef.current(data));

    const pedirSeVisivel = () => {
      // Aba em segundo plano não consulta. Quem estiver na frente é que dita o ritmo.
      if (
        !runWhenHidden &&
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible'
      ) {
        return;
      }
      requestPoll(spec);
    };

    pedirSeVisivel();
    const interval = setInterval(pedirSeVisivel, intervalMs);
    // Voltou ao foco: pede na hora, sem esperar o próximo ciclo.
    document.addEventListener('visibilitychange', pedirSeVisivel);

    return () => {
      unsubscribe();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', pedirSeVisivel);
    };
  }, [key, url, intervalMs, method, bodyKey, runWhenHidden]);
}
